import { Component, signal, ChangeDetectionStrategy } from '@angular/core';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { Observable } from 'rxjs';
import { OuiButtonModule } from 'omnium-ui/button';
import { OuiTextfield } from 'omnium-ui/form-field';
import { OuiSelectFilter } from 'omnium-ui/select-filter';
import { OuiTableModule } from 'omnium-ui/table';
import { OuiTagModule } from 'omnium-ui/tag';
import { appRoutePaths } from '../../../../app.paths';
import { ConsultantCardComponent } from '../../../../core/components/consultant-card/consultant-card.component';
import { InvestorCardComponent } from '../../../../core/components/investor-card/investor-card.component';
import { InvestorViewerSearchPage } from '../../../../core/models/api.models';
import { DoublonsBusterApiService } from '../../../../core/services/doublons-buster-api.service';
import { atlasInvestorStatusOptions } from '../shared/atlas-investor-filter-options';
import { InvestorStatusTagComponent } from '../../shared/investor-status-tag/investor-status-tag.component';
import { StateCardComponent } from '../../shared/state-card/state-card.component';
import { AtlasViewerSearchPageBase, atlasViewerSearchPaginatorProvider } from '../shared/atlas-viewer-search-page.base';

@Component({
  selector: 'dbw-atlas-investor-search-page',
  imports: [
    MatPaginatorModule,
    MatTableModule,
    OuiButtonModule,
    OuiSelectFilter,
    OuiTableModule,
    OuiTagModule,
    OuiTextfield,
    ConsultantCardComponent,
    InvestorCardComponent,
    InvestorStatusTagComponent,
    StateCardComponent,
  ],
  providers: [atlasViewerSearchPaginatorProvider],
  templateUrl: './atlas-viewer.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './atlas-viewer.component.scss',
})
export class AtlasInvestorSearchPageComponent extends AtlasViewerSearchPageBase<InvestorViewerSearchPage> {
  readonly appRoutePaths = appRoutePaths;
  readonly displayedColumns = ['investor', 'birthDate', 'status', 'consultant', 'arianeId'];
  readonly investorIdFilter = signal('');
  readonly investorCodeFilter = signal('');
  readonly investorNameFilter = signal('');
  readonly investorStatusFilter = signal<string[]>([]);
  readonly arianeIdFilter = signal('');
  readonly consultantIdFilter = signal('');
  readonly consultantCodeFilter = signal('');
  readonly consultantNameFilter = signal('');
  readonly investorStatusOptions = atlasInvestorStatusOptions;

  constructor(private readonly apiService: DoublonsBusterApiService) {
    super();
  }

  onInvestorIdFilterChange(value: string): void {
    this.investorIdFilter.set(value);
    this.queueFilterReload();
  }

  onInvestorCodeFilterChange(value: string): void {
    this.investorCodeFilter.set(value);
    this.queueFilterReload();
  }

  onInvestorNameFilterChange(value: string): void {
    this.investorNameFilter.set(value);
    this.queueFilterReload();
  }

  onInvestorStatusFilterChange(value: string[]): void {
    this.investorStatusFilter.set(value);
    this.queueFilterReload();
  }

  onArianeIdFilterChange(value: string): void {
    this.arianeIdFilter.set(value);
    this.queueFilterReload();
  }

  onConsultantIdFilterChange(value: string): void {
    this.consultantIdFilter.set(value);
    this.queueFilterReload();
  }

  onConsultantCodeFilterChange(value: string): void {
    this.consultantCodeFilter.set(value);
    this.queueFilterReload();
  }

  onConsultantNameFilterChange(value: string): void {
    this.consultantNameFilter.set(value);
    this.queueFilterReload();
  }

  override hasActiveFilters(): boolean {
    return Boolean(
      this.investorIdFilter().trim() ||
      this.investorCodeFilter().trim() ||
      this.investorNameFilter().trim() ||
      this.investorStatusFilter().length > 0 ||
      this.arianeIdFilter().trim() ||
      this.consultantIdFilter().trim() ||
      this.consultantCodeFilter().trim() ||
      this.consultantNameFilter().trim()
    );
  }

  protected override loadPageRequest(pageIndex: number, pageSize: number): Observable<InvestorViewerSearchPage> {
    return this.apiService.searchAtlasInvestors(pageIndex, pageSize, {
      investorId: this.parseOptionalNumber(this.investorIdFilter()),
      investorCode: this.investorCodeFilter(),
      investorName: this.investorNameFilter(),
      arianeId: this.parseOptionalNumber(this.arianeIdFilter()),
      investorStatuses: this.investorStatusFilter(),
      consultantId: this.parseOptionalNumber(this.consultantIdFilter()),
      consultantCode: this.consultantCodeFilter(),
      consultantName: this.consultantNameFilter(),
    });
  }

  protected override getLoadErrorMessage(error: unknown): string {
    const httpError = error as { error?: { detail?: string } };
    return httpError?.error?.detail ?? 'Impossible de rechercher les investisseurs Atlas.';
  }
}
